import client, { API_BASE_URL } from './client';

const PATH = '/api/notifications/';
const ADMIN_PATH = '/api/admin/notifications/';

const DEVICE_KEY_STORAGE = 'av_notif_device';
const DEVICE_KEY_BYTES = 16;

// Anonymous visitors are identified to the backend by a random key held here.
// It carries no personal data; it only lets the server keep read state for a
// browser that has not signed in. Logged-in users are tracked by their account
// and do not need it.
const loadDeviceKey = () => {
  try {
    const existing = localStorage.getItem(DEVICE_KEY_STORAGE);
    if (existing && /^[a-f0-9]{16,64}$/.test(existing)) return existing;
    const bytes = new Uint8Array(DEVICE_KEY_BYTES);
    window.crypto.getRandomValues(bytes);
    const key = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem(DEVICE_KEY_STORAGE, key);
    return key;
  } catch (err) {
    return null;
  }
};

let cachedDeviceKey;

const notificationDeviceHeaders = () => {
  if (cachedDeviceKey === undefined) cachedDeviceKey = loadDeviceKey();
  return cachedDeviceKey ? { 'X-Notification-Device': cachedDeviceKey } : {};
};

export const notificationApi = {
  // Public
  getConfig() {
    return client.get(`${PATH}config/`).then((r) => r.data);
  },

  listNotifications(params = {}) {
    return client.get(PATH, { params, headers: notificationDeviceHeaders() }).then((r) => r.data);
  },

  // Read state is stored server-side so it follows the account across devices.
  markRead(ids) {
    return client
      .post(`${PATH}read/`, { ids }, { headers: notificationDeviceHeaders() })
      .then((r) => r.data);
  },

  // Marks every notification the viewer can currently see, so the bell does
  // not have to page through the feed to discover what is unread.
  markAllRead(parivarId = null) {
    const payload = { all: true };
    if (parivarId) payload.parivar_id = parivarId;
    return client
      .post(`${PATH}read/`, payload, { headers: notificationDeviceHeaders() })
      .then((r) => r.data);
  },

  registerSubscription(subscription, parivarId = null) {
    const payload = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: subscription.getKey('p256dh') ? btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))) : '',
        auth: subscription.getKey('auth') ? btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')))) : '',
      },
    };
    if (parivarId) payload.parivar_id = parivarId;
    return client.post(`${PATH}subscription/`, payload).then((r) => r.data);
  },

  removeSubscription(endpoint) {
    return client.delete(`${PATH}subscription/`, { params: { endpoint } }).then((r) => r.data);
  },

  // Admin
  adminList(params = {}) {
    return client.get(ADMIN_PATH, { params }).then((r) => r.data);
  },

  adminCreate(payload) {
    return client.post(ADMIN_PATH, payload).then((r) => r.data);
  },

  adminUpdate(id, payload) {
    return client.patch(`${ADMIN_PATH}${id}/`, payload).then((r) => r.data);
  },

  adminRemove(id) {
    return client.delete(`${ADMIN_PATH}${id}/`).then((r) => r.data);
  },

  sendNow(id) {
    return client.post(`${ADMIN_PATH}${id}/send_now/`).then((r) => r.data);
  },

  cancel(id) {
    return client.post(`${ADMIN_PATH}${id}/cancel/`).then((r) => r.data);
  },

  getAdminConfig() {
    return client.get(`${ADMIN_PATH}config/`).then((r) => r.data);
  },

  updateAdminConfig(payload) {
    return client.patch(`${ADMIN_PATH}config/`, payload).then((r) => r.data);
  },

  sendTest() {
    return client.post(`${ADMIN_PATH}config/test/`).then((r) => r.data);
  },
};

/** Strip trailing slash so comparisons against the API base are consistent. */
export const NOTIFICATIONS_BASE = API_BASE_URL.replace(/\/$/, '');
export default notificationApi;